import React, { useEffect, useState } from "react";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import BodyHeader from "../../../../components/common/CommonBodyHeader";
import { FormikProvider, useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
   fetchSingleCustomerMasterAsync,
   updateCustomerMasterAsync,
} from "../../../../redux/features/customerMaster";
import { getFleetMasterDropdownDataAsync } from "../../../../redux/features/fleetMaster";
import { useParams } from "react-router-dom";
import ContactPerson from "./ContactPerson";
import AgreementDocument from "./AgreementDocument";
import Location from "./Location";
import Lane from "./Lane";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import { useToggleState } from "../../../../Hooks/useToggleState";
import toast from "react-hot-toast";
import * as Yup from "yup";
import validation from "../../../../helpers/validation";
import CustomDropdown from "../../../../components/common/CustomDropdown/CustomDropdown";

// (locations/addresses/documents/contact_person_details/lane_masters)
const TABLE_NAMES = {
   locations: "locations",
   addresses: "addresses",
   documents: "documents",
   contact_person_details: "contact_person_details",
   lane_masters: "lane_masters",
};

const initialValues = {
   customerName: "",
   gstNumber: "",
   panNumber: "",

   agreementDocument: [""],
   contactPerson: [
      {
         id: "",
         name: "",
         mobile: "",
         email: "",
      },
   ],
   productType: "",
   // productTypeOtherValue:"",

   location: [""],
   address: [""],
   lane: [
      {
         laneName: "",
         origin: "",
         destination: "",
         vehicleType: "",
         tonnage: "",
         expressModeRateAdditional: "",
         superExpressModeRateAdditional: "",
         detentionRateAdditional: "",
         multipleLoadingLocationRateAdditional: "",
         multipleUnloadingLocationRateAdditional: "",
         loadingCharges: "",
         unloadingCharge: "",
         miscellaneousCharges: "",
         miscellaneousRemarks: "",
      },
   ],
};

function CustomerMasterEditForm() {
   const params = useParams();
   const dispatch = useDispatch();

   const fleetMaster = useSelector((state) => state.fleetMaster);
   const customerMaster = useSelector((state) => state.customerMaster);
   const singleCustomerMaster = customerMaster?.singleCustomerMaster;

   const locationData = fleetMaster?.location?.map?.((item) => ({
      label: item?.name,
      value: item?.id,
   }));

   const productType = ["LPG", "Petrol", "Diesel", "Ethanol", "Lubricant", "Other"].map(
      (item) => ({
         label: item,
         value: item,
      }),
   );

   const vehicleTypeData = fleetMaster?.vehicleCategory?.map?.((item) => ({
      label: item?.category_name,
      value: item?.id,
   }));

   const tonnageData = Object.values(fleetMaster?.tonnage)?.map((item) => ({
      label: item?.tonnage,
      value: item?.id,
   }));

   const [initialState, setInitialState] = useState(initialValues);
   const [isLoading, startLoading, stopLoading] = useToggleState(true);

   useEffect(() => {
      dispatch(getFleetMasterDropdownDataAsync());
   }, [dispatch]);

   useEffect(() => {
      dispatch(backdropLoadingAction(true));
      startLoading();

      dispatch(
         fetchSingleCustomerMasterAsync({
            id: params?.id,
            err: () => {
               stopLoading();
               dispatch(backdropLoadingAction(false));
            },
            done: () => {
               stopLoading();
               dispatch(backdropLoadingAction(false));
            },
         }),
      );
   }, [dispatch, params?.id]);

   const getSelectedVehicleCategory = () => {
      if (!singleCustomerMaster?.vehicle_type || !vehicleTypeData) return [];

      const selectedIds = singleCustomerMaster.vehicle_type
         .split(",")
         .filter(Boolean)
         .map(Number);

      return vehicleTypeData.filter((item) => selectedIds.includes(item.value));
   };

   useEffect(() => {
      !isLoading &&
         setInitialState((prev) => ({
            ...prev,
            customerName: singleCustomerMaster?.name,
            gstNumber: singleCustomerMaster?.gst_number,
            panNumber: singleCustomerMaster?.pan_number,

            productType: singleCustomerMaster?.product_type ?? "",

            productTypeOtherValue: singleCustomerMaster?.product_type,
         }));

      //console.log("here",singleCustomerMaster);
   }, [singleCustomerMaster?.name, isLoading]);

   const formik = useFormik({
      initialValues: initialState,
      enableReinitialize: true,
      validationSchema: Yup.object().shape({
         customerName: validation.name("Customer Name"),
         gstNumber: validation.gstNumber.notRequired(),
         panNumber: Yup.string().required("PAN number is required"),
      }),
      onSubmit: handleUpdate,
   });

   const {
      touched,
      errors,
      values,
      getFieldProps,
      handleSubmit,
      resetForm,
      setFieldValue,
   } = formik;

   function handleUpdate(formdata) {
      const newData = new FormData();

      const data = {
         id: params?.id,
         name: formdata.customerName,
         gst_number: formdata.gstNumber,
         pan_number: formdata.panNumber,
         product_type: formdata?.productType,
         other_value: formdata?.productTypeOtherValue,
         model_id: 6,
         action_id: 2,
         _method: "PATCH",
      };

      Object.keys(data).forEach((key) => {
         newData.append(key, data[key]);
      });

      dispatch(backdropLoadingAction(true));
      dispatch(
         updateCustomerMasterAsync({
            data: newData,
            err: () => {
               //toast.error("Something went wrong !");
               dispatch(backdropLoadingAction(false));
            },
            done: () => {
               toast.success("Record updated successfully !");
               dispatch(backdropLoadingAction(false));
            },
         }),
      );
   }

   return (
      <FormikProvider value={formik}>
         <div>
            <BodyHeader title="Edit Customer Master" />
            <div className="shadow-lg">
               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Customer Details</h3>
                  </div>
                  <div className="card-body">
                     <div className="row">
                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="Customer Name"
                              id="customerName"
                              placeholder="Enter Customer Name"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.customerName}
                              errors={touched?.customerName && errors?.customerName}
                              message={errors?.customerName}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomInput
                              label="GST Number"
                              id="gstNumber"
                              name="gstNumber"
                              placeholder="Enter GST Number"
                              {...getFieldProps(`gstNumber`)}
                              errors={
                                 Boolean(touched.gstNumber) && Boolean(errors.gstNumber)
                              }
                              message={errors.gstNumber}
                           />
                        </div>

                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="Pan Number"
                              id="panNumber"
                              name="panNumber"
                              placeholder="Enter PAN Number"
                              {...getFieldProps(`panNumber`)}
                              errors={
                                 Boolean(touched.panNumber) && Boolean(errors.panNumber)
                              }
                              message={errors.panNumber}
                           />
                        </div>

                        <div className="col-lg-4">
                           <CustomDropdown
                              optionData={productType}
                              label="Product Type"
                              values={
                                 values.productType
                                    ? [
                                         {
                                            label: values.productType,
                                            value: values.productType,
                                         },
                                      ]
                                    : []
                              }
                              onChange={(selected) => {
                                 setFieldValue("productType", selected?.[0]?.value);
                              }}
                              errors={
                                 Boolean(touched.productType) &&
                                 Boolean(errors.productType)
                              }
                              message={errors.productType}
                           />
                        </div>

                        {values.productType === "Other" && (
                           <div className="col-lg-4">
                              <CustomInput
                                 label="Other Value"
                                 name="productTypeOtherValue"
                                 id="productTypeOtherValue"
                                 {...getFieldProps(`productTypeOtherValue`)}
                                 errors={
                                    Boolean(touched.productTypeOtherValue) &&
                                    Boolean(errors.productTypeOtherValue)
                                 }
                                 message={errors.productTypeOtherValue}
                              />
                           </div>
                        )}

                        <div className="mt-3">
                           <button
                              type="button"
                              className="btn btn-success ml-2 mt-3 "
                              onClick={handleSubmit}
                           >
                              Update
                           </button>
                        </div>

                        {/* Agreement Details */}
                        <div className="col-lg-12">
                           <AgreementDocument
                              agreementDocumentList={singleCustomerMaster?.documents}
                           />

                           {/* Contact Person Details  */}
                           <ContactPerson
                              contactPersonList={
                                 singleCustomerMaster?.contact_personal_details
                              }
                           />
                           <Location
                              locationList={singleCustomerMaster?.locations}
                              locationData={locationData}
                           />
                           <Lane
                              laneList={singleCustomerMaster?.lane}
                              tableName={TABLE_NAMES.lane_masters}
                              locationData={locationData}
                              vehicleTypeData={vehicleTypeData}
                              tonnageData={tonnageData}
                              customerName={singleCustomerMaster?.name}
                           />
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </FormikProvider>
   );
}

export default CustomerMasterEditForm;
